/**
 * Deep Linking — handles incoming URLs when the app is opened via
 * Universal Links (iOS) or App Links (Android).
 *
 * Call `initDeepLinking(router)` once from a root layout or client
 * component to start listening for URL-open events.
 */

import { isNativePlatform } from '@/lib/utils/platform'

type RouterLike = {
  push: (url: string) => void
}

/** Known route patterns that we can deep-link into */
const ROUTE_PATTERNS = [
  // /in/:orgSlug/members/:id
  /^\/in\/[^/]+\/members\/[^/]+$/,
  // /in/:orgSlug/events/:slug
  /^\/in\/[^/]+\/events\/[^/]+$/,
  // /in/:orgSlug/chapters/:slug
  /^\/in\/[^/]+\/chapters\/[^/]+$/,
  // /in/:orgSlug/opportunities/:id (legacy shim — redirects to the jobs board)
  /^\/in\/[^/]+\/opportunities\/[^/]+$/,
  // /in/:orgSlug/jobs (board)
  /^\/in\/[^/]+\/jobs$/,
  // /in/:orgSlug/jobs/:slug
  /^\/in\/[^/]+\/jobs\/[^/]+$/,
  // /in/:orgSlug (dashboard)
  /^\/in\/[^/]+$/,
  // /in/:orgSlug/admin/members (action queue + list, preserves ?tab=…)
  /^\/in\/[^/]+\/admin\/members$/,
  // /in/:orgSlug/admin/members/:id
  /^\/in\/[^/]+\/admin\/members\/[^/]+$/,
  // /in/:orgSlug/admin/events (list + filters)
  /^\/in\/[^/]+\/admin\/events$/,
  // /in/:orgSlug/admin/events/:eventId
  /^\/in\/[^/]+\/admin\/events\/[^/]+$/,
  // /settings
  /^\/settings/,
  // /login, /signup
  /^\/(login|signup)/,
]

interface ExtractedTarget {
  path: string
  pathWithQuery: string
}

/**
 * Extract the in-app pathname (and querystring) from an incoming URL.
 * Returns the path used for matching and the path-plus-query used for routing.
 */
function extractPath(url: string): ExtractedTarget | null {
  try {
    const parsed = new URL(url)
    return {
      path: parsed.pathname,
      pathWithQuery: parsed.pathname + parsed.search,
    }
  } catch {
    // If it's already a path, use it directly. Split on '?' so the matcher
    // sees only the pathname, but we still preserve the query for the push.
    if (url.startsWith('/')) {
      const [path] = url.split('?')
      return { path, pathWithQuery: url }
    }
    return null
  }
}

/**
 * Check if a path matches a known deep-linkable route.
 */
function isKnownRoute(path: string): boolean {
  return ROUTE_PATTERNS.some((pattern) => pattern.test(path))
}

/**
 * Initialize deep link listener. Call once from a root client component.
 *
 * @returns Cleanup function to remove listener
 */
export async function initDeepLinking(router: RouterLike): Promise<() => void> {
  if (!isNativePlatform()) return () => {}

  try {
    const { App } = await import('@capacitor/app')

    // Handle URLs that open the app while it's running
    const listener = await App.addListener('appUrlOpen', ({ url }) => {
      const target = extractPath(url)
      if (target && isKnownRoute(target.path)) {
        router.push(target.pathWithQuery)
      }
    })

    return () => {
      listener.remove()
    }
  } catch {
    // Deep linking is non-critical — degrade gracefully
    return () => {}
  }
}

/**
 * Handle the app being resumed from background.
 * Useful for refreshing stale data.
 *
 * @returns Cleanup function to remove listener
 */
export async function onAppResume(callback: () => void): Promise<() => void> {
  if (!isNativePlatform()) return () => {}

  try {
    const { App } = await import('@capacitor/app')
    const listener = await App.addListener('resume', callback)
    return () => {
      listener.remove()
    }
  } catch {
    return () => {}
  }
}

/**
 * Handle back button press (Android only).
 * Return `true` to prevent default behavior.
 *
 * @returns Cleanup function to remove listener
 */
export async function onBackButton(callback: (canGoBack: boolean) => void): Promise<() => void> {
  if (!isNativePlatform()) return () => {}

  try {
    const { App } = await import('@capacitor/app')
    const listener = await App.addListener('backButton', ({ canGoBack }) => {
      callback(canGoBack)
    })
    return () => {
      listener.remove()
    }
  } catch {
    return () => {}
  }
}
